import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { clone, sortBy } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import validate from 'utils/validate';
import CourierZone from '../../components/CourierZone';
import FormAdd2 from '../../components/FormAdd2';
import { dspSelector, LOADED_LIST_DSP } from '../../reducers/DspReducer';
import { getDsp, listDsp, updateDsp } from '../../repositories/HttpRepository';
import FormatPhoneNumber from '../../utils/FormatPhoneNumber';
import { getRegions } from '../../atoms/BookingTemplate';
import { COURIER_TYPES, getFormMetadata, VALIDATE_RULES } from './FormMetadata';
import { userState } from 'atoms/States';
import { getSelectorPlatform } from 'atoms/DspState';

const useInputStyles = makeStyles()(
  {
    root: {
      fontWeight: 600,
      fontFamily: 'AvenirNext',
      fontStretch: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontSize: 14,
    },
  },
  { name: 'MuiInputBase' },
);

const useInputLabelStyles = makeStyles()(
  {
    root: {
      fontWeight: 600,
      fontFamily: 'AvenirNext',
      fontStretch: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontSize: 14,
    },
  },
  { name: 'MuiFormLabel' },
);

const useStyles = makeStyles()((theme) => ({
  DialogAction: {
    margin: '10px 16px',
  },
  title: {
    fontWeight: 700,
  },
  closeBtn: {
    position: 'absolute',
    right: 20,
    top: 15,
    color: '#d8d8d8',
    fontSize: 30,
    cursor: 'pointer',
  },
  CancelBtn: {
    border: 'solid 1px #4a4a4a',
    backgroundColor: '#ffffff',
    padding: '3px 15px',
    color: 'black',
  },
  SaveBtn: {
    backgroundColor: '#cccccc !important',
    padding: '4px 25px',
    color: '#fff',
    '&.active': {
      backgroundColor: '#4abc4e !important',
    },
  },
  DialogContent: {
    overflowY: 'initial',
  },
  tabs: {
    marginBottom: 15,
  },
}));

const DSP_RATES_SCOPES = ['dsp-rates-manager', 'dsp-rates-admin'];

const UpdateCourier = () => {
  const regions = useRecoilValue(getRegions);
  const updateValues = useSetRecoilState(dspSelector);
  const user = useRecoilValue(userState);
  const platforms = useRecoilValue(getSelectorPlatform);
  const optPlatforms =
    platforms?.courier_platforms
      ?.split(',')
      ?.filter(Boolean)
      ?.map((p) => ({ label: p?.toUpperCase(), value: p })) || [];
  const [courier, setCourier] = useState({
    username: '',
    password: '',
    email: '',
    company: '',
    phone_number: '',
    regions: [],
    type: '',
  });
  const [errorForm, setErrorForm] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [tab, setTab] = useState('settings');
  const navigate = useNavigate();
  const { classes } = useStyles();
  useInputStyles();
  useInputLabelStyles();
  const { scopes } = user;

  const isExistsDSPRate = scopes?.some((s) => DSP_RATES_SCOPES.includes(s));

  const handleSelectRegionChange = (_, selected) => {
    const newCourierData = { ...courier };
    newCourierData.regions = selected.map(({ value }) => value);
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['regions'] = '';
    setErrorForm(clearError);
  };

  const handleSelectTypeChange = (_, selected) => {
    const newCourierData = { ...courier };
    newCourierData.type = selected.props.value;
    newCourierData.platform = '';
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['type'] = '';
    setErrorForm(clearError);
  };

  const handlePlatformChange = (_, selected) => {
    const newCourierData = { ...courier };
    newCourierData.platform = selected.props.value;
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['platform'] = '';
    setErrorForm(clearError);
  };

  const { id } = useParams();
  const regionOptions = sortBy(
    regions?.map(({ properties }) => ({
      label: `[${properties.code}] ${properties.display_name}`,
      value: properties.code,
    })),
    'value',
  );

  useEffect(() => {
    getDsp(id).then((resp) => {
      if (resp.ok) {
        let tmp = resp.data.courier;
        tmp['username'] = resp.data.user.username;
        setCourier(tmp);
      }
    });
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    let tmp = courier;
    tmp[name] = value;
    setCourier(tmp);
    // clear error
    const clearError = clone(errorForm);
    clearError[name] = '';
    setErrorForm(clearError);
  };

  const submitForm = useCallback(() => {
    if (isSubmit) return;
    setIsSubmit(true);
    //format phone number
    let data = { ...courier };
    const newPhoneNumber = FormatPhoneNumber(courier.phone_number);
    data.phone_number = newPhoneNumber;
    setCourier({ ...courier, phone_number: newPhoneNumber });
    let rules = VALIDATE_RULES;
    delete rules.username;
    delete rules.password;
    if (data.type === 'DSP') {
      delete rules.platform;
    }
    const error = validate(data, rules);
    if (error) {
      setErrorForm(error);
      setIsSubmit(false);
    } else {
      updateDsp(id, data).then((res) => {
        setIsSubmit(false);
        if (res.ok) {
          listDsp().then((resp) => {
            if (resp.ok) {
              updateValues({
                type: LOADED_LIST_DSP,
                data: resp.data,
                params: { page: 0, size: 20, q: '' },
              });
              toast.success('DSP updated successfully');
              navigate('/dsp');
            }
          });
        } else if (res?.data?.errors) {
          toast.error(res.data.errors[0]);
        } else {
          toast.error('Server is having problem, please try again later.');
        }
      });
    }
  }, [courier, isSubmit]);

  function handleCancel() {
    navigate('/dsp');
  }

  function joinClassnames(...classnames) {
    return classnames.filter((i) => i).join(' ');
  }

  const handleSetupRates = () => {
    window.open(`${process.env.REACT_APP_PAYMENT_URL}/dsp-payment/summary/${id}/settings`, '_blank');
  };

  return (
    <Dialog open={true} maxWidth="md" fullWidth onClose={() => navigate('/dsp')} disableEscapeKeyDown aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        <Typography variant="h4" className={classes.title}>
          Edit DSP Courier
          <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.DialogContent}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} className={classes.tabs} centered indicatorColor="primary">
          <Tab label="Settings" value="settings" />
          <Tab label="Delivery Zone" value="zones" />
        </Tabs>
        {tab === 'settings' && (
          <FormAdd2
            handleChange={handleChange}
            error={errorForm}
            list={getFormMetadata(true, regionOptions, COURIER_TYPES, handleSelectTypeChange, handleSelectRegionChange, optPlatforms, handlePlatformChange, courier.type)}
            datas={courier}
          />
        )}
        {tab === 'zones' && <CourierZone courier={courier} />}
      </DialogContent>
      {tab === 'settings' && (
        <DialogActions className={classes.DialogAction}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: isExistsDSPRate ? 'space-between' : 'flex-end',
              flex: 1,
            }}
          >
            {isExistsDSPRate && (
              <Button onClick={() => handleSetupRates()} color="primary" variant="contained" disableElevation>
                SETUP RATES
              </Button>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                className={classes.CancelBtn}
                onClick={() => {
                  navigate('/dsp');
                }}
              >
                CANCEL
              </Button>
              <Button className={joinClassnames(classes.SaveBtn, 'active')} onClick={submitForm} disabled={isSubmit}>
                UPDATE
              </Button>
            </Box>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UpdateCourier;
